import { useCallback, useState } from 'react';
import { StateManager } from '../managers/StateManager';
import { parsePinLabels, buildPins } from '../utils/pins';
import { ContextMenuHandlers } from './pixi/types';

type ContextMenuItem = { label: string; action: () => void; dangerous?: boolean; closeOnClick?: boolean };

interface ContextMenuState {
  x: number;
  y: number;
  items: ContextMenuItem[];
}

interface InputModalState {
  title: string;
  label: string;
  initialValue: string;
  onConfirm: (value: string) => void;
}

/**
 * Owns the context-menu and modal state for the harness and the action handlers
 * the PIXI layer triggers (rename/delete connectors, edit/delete wires, add
 * entities). Deletions apply immediately without confirmation since they can be
 * reversed with undo. Returns the current menu/modal state plus the handler
 * bundle passed into the PIXI canvas.
 */
export function useHarnessInteractions(stateManager: StateManager) {
  const [contextMenu, setContextMenu] = useState<ContextMenuState | null>(null);
  const [inputModal, setInputModal] = useState<InputModalState | null>(null);
  const [wireEditId, setWireEditId] = useState<string | null>(null);
  const [sectionEdit, setSectionEdit] = useState<{ aId: string; bId: string } | null>(null);

  const showContextMenu = useCallback((x: number, y: number, items: ContextMenuItem[]) => {
    setContextMenu({ x, y, items });
  }, []);

  const hideContextMenu = useCallback(() => setContextMenu(null), []);

  const showInputModal = useCallback((title: string, label: string, initialValue: string, onConfirm: (value: string) => void) => {
    setInputModal({ title, label, initialValue, onConfirm });
  }, []);

  const hideInputModal = useCallback(() => setInputModal(null), []);

  const handleConnectorRename = useCallback((connectorId: string, currentName: string) => {
    showInputModal('Rename Connector', 'Connector name:', currentName, (newName) => {
      stateManager.updateConnector(connectorId, { name: newName });
      hideInputModal();
    });
  }, [stateManager, showInputModal, hideInputModal]);

  const handleConnectorDelete = useCallback((connectorId: string) => {
    stateManager.removeConnector(connectorId);
  }, [stateManager]);

  const handleConnectorEditPins = useCallback((connectorId: string) => {
    const connector = stateManager.getState().connectors.find(c => c.id === connectorId);
    if (!connector) {
      return;
    }
    const currentLabels = (connector.pins ?? []).map(p => p.label).join(', ');
    showInputModal('Edit Pins', 'Pin labels (comma or space separated):', currentLabels, (value) => {
      const labels = parsePinLabels(value);
      const pins = buildPins(labels, connector.pins ?? [], () => stateManager.generateUniqueId());
      stateManager.setConnectorPins(connectorId, pins);
      hideInputModal();
    });
  }, [stateManager, showInputModal, hideInputModal]);

  const handleApplyConnectorType = useCallback((connectorId: string, typeId: string) => {
    const state = stateManager.getState();
    const connector = state.connectors.find(c => c.id === connectorId);
    const type = state.connectorTypes.find(t => t.id === typeId);
    if (!connector || !type) {
      return;
    }
    // Reuse an existing pin's id when its number is unchanged so wire assignments
    // survive, while adopting the type's pin names.
    const byLabel = new Map((connector.pins ?? []).map(p => [p.label, p]));
    const pins = type.pins.map(typePin => {
      const existing = byLabel.get(typePin.label);
      return existing
        ? { ...existing, name: typePin.name }
        : { id: stateManager.generateUniqueId(), label: typePin.label, name: typePin.name };
    });
    stateManager.beginBatch();
    stateManager.setConnectorPins(connectorId, pins);
    stateManager.updateConnector(connectorId, { connectorTypeId: typeId });
    stateManager.endBatch();
  }, [stateManager]);

  const handleAddConnectorFromType = useCallback((typeId: string, x: number, y: number) => {
    const type = stateManager.getState().connectorTypes.find(t => t.id === typeId);
    if (!type) {
      return;
    }
    const pins = type.pins.map(p => ({ id: stateManager.generateUniqueId(), label: p.label, name: p.name }));
    stateManager.addConnector({
      id: stateManager.generateUniqueId(),
      name: type.name,
      xPosition: x,
      yPosition: y,
      connectorTypeId: typeId,
      pins
    });
  }, [stateManager]);

  const handleNodeDelete = useCallback((nodeId: string) => {
    stateManager.removeNode(nodeId);
  }, [stateManager]);

  const handleAddConnector = useCallback((x: number, y: number) => {
    stateManager.addConnector({
      id: stateManager.generateUniqueId(),
      name: 'New Connector',
      xPosition: x,
      yPosition: y
    });
  }, [stateManager]);

  const handleAddNode = useCallback((x: number, y: number) => {
    stateManager.addNode({
      id: stateManager.generateUniqueId(),
      xPosition: x,
      yPosition: y
    });
  }, [stateManager]);

  const hideWireEdit = useCallback(() => setWireEditId(null), []);

  const handleWireEdit = useCallback((wireId: string) => {
    setWireEditId(wireId);
  }, []);

  const handleWireDelete = useCallback((wireId: string) => {
    stateManager.removeWire(wireId);
  }, [stateManager]);

  const handleWireHighlight = useCallback((wireId: string) => {
    // Toggle a sticky highlight of the wire's route; hovering the wire table
    // drives the same channel for transient highlighting.
    const current = stateManager.getInteractionState().highlightedWireId;
    stateManager.setHighlightedWire(current === wireId ? undefined : wireId);
  }, [stateManager]);

  const hideSectionEdit = useCallback(() => setSectionEdit(null), []);

  const handleSectionEdit = useCallback((aId: string, bId: string) => {
    setSectionEdit({ aId, bId });
  }, []);

  const contextMenuHandlers: ContextMenuHandlers = {
    showContextMenu,
    handleConnectorRename,
    handleConnectorDelete,
    handleConnectorEditPins,
    handleApplyConnectorType,
    handleNodeDelete,
    handleAddConnector,
    handleAddNode,
    handleWireEdit,
    handleWireDelete,
    handleWireHighlight,
    handleSectionEdit
  };

  return {
    contextMenu,
    hideContextMenu,
    inputModal,
    hideInputModal,
    wireEditId,
    hideWireEdit,
    sectionEdit,
    hideSectionEdit,
    handleAddConnectorFromType,
    contextMenuHandlers
  };
}
